import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "☁️",
    title: "Cloud Migration & Modernisation",
    desc: "Lift-and-shift, re-platform, or full cloud-native refactoring — we migrate your workloads to AWS, GCP, or Azure with zero downtime, reduced costs, and future-proof architecture.",
    tags: ["AWS", "GCP", "Azure", "Terraform"],
    color: "#0EA5E9",
  },
  {
    icon: "🔄",
    title: "CI/CD Pipeline Automation",
    desc: "End-to-end automated build, test, and deployment pipelines using GitHub Actions, GitLab CI, or Jenkins — shipping code to production in minutes, not days.",
    tags: ["GitHub Actions", "Jenkins", "ArgoCD", "GitLab CI"],
    color: "#8B5CF6",
  },
  {
    icon: "🐳",
    title: "Containerisation & Kubernetes",
    desc: "Docker containerisation and Kubernetes orchestration for auto-scaling, self-healing deployments with multi-cluster management, Helm charts, and service mesh integration.",
    tags: ["Docker", "Kubernetes", "Helm", "Istio"],
    color: "#3B82F6",
  },
  {
    icon: "🏗️",
    title: "Infrastructure as Code",
    desc: "Reproducible, version-controlled infrastructure using Terraform, Pulumi, and CloudFormation — eliminating manual provisioning and configuration drift across all environments.",
    tags: ["Terraform", "Pulumi", "Ansible", "CloudFormation"],
    color: "#10B981",
  },
  {
    icon: "📊",
    title: "Observability & Monitoring",
    desc: "Full-stack observability with metrics, logs, and distributed tracing — custom dashboards, intelligent alerting, and SLO/SLA tracking so issues are caught before customers notice.",
    tags: ["Prometheus", "Grafana", "Datadog", "OpenTelemetry"],
    color: "#F59E0B",
  },
  {
    icon: "🔐",
    title: "DevSecOps & Compliance",
    desc: "Security baked into every stage of the pipeline — SAST, DAST, container scanning, secrets management, and compliance-as-code for SOC2, ISO 27001, HIPAA, and PCI-DSS.",
    tags: ["Vault", "Trivy", "SonarQube", "OPA"],
    color: "#EF4444",
  },
  {
    icon: "⚡",
    title: "Serverless & Edge Computing",
    desc: "Event-driven architectures with AWS Lambda, GCP Cloud Run, and Cloudflare Workers — auto-scaling to zero, pay-per-use billing, and sub-millisecond global edge latency.",
    tags: ["Lambda", "Cloud Run", "Cloudflare", "EventBridge"],
    color: "#EC4899",
  },
  {
    icon: "💾",
    title: "Cloud Cost Optimisation",
    desc: "FinOps practices and automated cost governance — reserved instance planning, spot fleet management, right-sizing recommendations, and budget alerts that cut cloud bills by 40%+.",
    tags: ["AWS Cost Explorer", "Kubecost", "Infracost", "FinOps"],
    color: "#06B6D4",
  },
];

const TECH_STACK = [
  {
    category: "Cloud Platforms",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Google Cloud", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
      { name: "Azure", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "DigitalOcean", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/digitalocean/digitalocean-original.svg", color: "#0080FF" },
      { name: "Cloudflare", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cloudflare/cloudflare-original.svg", color: "#F38020" },
      { name: "Linode", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg", color: "#00A95C" },
    ],
  },
  {
    category: "Containers & Orchestration",
    items: [
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "Helm", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/helm/helm-original.svg", color: "#0F1689" },
      { name: "Podman", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/podman/podman-original.svg", color: "#892CA0" },
      { name: "ArgoCD", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/argocd/argocd-original.svg", color: "#EF7B4D" },
      { name: "Rancher", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg", color: "#0075A8" },
    ],
  },
  {
    category: "CI/CD & IaC",
    items: [
      { name: "Terraform", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg", color: "#7B42BC" },
      { name: "Ansible", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/ansible/ansible-original.svg", color: "#EE0000" },
      { name: "GitHub Actions", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg", color: "#2088FF" },
      { name: "GitLab CI", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/gitlab/gitlab-original.svg", color: "#FC6D26" },
      { name: "Jenkins", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jenkins/jenkins-original.svg", color: "#D33833" },
      { name: "Pulumi", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#8A3391" },
    ],
  },
  {
    category: "Monitoring & Security",
    items: [
      { name: "Prometheus", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/prometheus/prometheus-original.svg", color: "#E6522C" },
      { name: "Grafana", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/grafana/grafana-original.svg", color: "#F46800" },
      { name: "Datadog", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/datadog/datadog-original.svg", color: "#632CA6" },
      { name: "Vault", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg", color: "#FFD814" },
      { name: "SonarQube", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/sonarqube/sonarqube-original.svg", color: "#4E9BCD" },
      { name: "Elasticsearch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/elasticsearch/elasticsearch-original.svg", color: "#005571" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Cloud Readiness Assessment", desc: "We audit your existing infrastructure, dependencies, and workloads — identifying migration blockers, security gaps, and quick wins before writing a single line of IaC.", color: "#0EA5E9" },
  { n: "02", icon: "🏗️", title: "Architecture & Roadmap Design", desc: "Cloud-native architecture blueprints aligned with your SLOs, compliance requirements, and budget — designed for day-two operations, not just day-one deployment.", color: "#8B5CF6" },
  { n: "03", icon: "⚙️", title: "Infrastructure Provisioning", desc: "Terraform or Pulumi modules provision every resource — VPCs, IAM, databases, compute, and networking — in reproducible, peer-reviewed, version-controlled code.", color: "#10B981" },
  { n: "04", icon: "🔄", title: "CI/CD Pipeline Build", desc: "Automated pipelines from commit to production: lint, unit test, integration test, container build, security scan, staging deploy, smoke test, and zero-downtime prod release.", color: "#F59E0B" },
  { n: "05", icon: "🚀", title: "Migration & Go-Live", desc: "Blue-green or canary cutover with real-time rollback triggers, data migration validation, and a dedicated war-room team monitoring every metric during go-live.", color: "#EF4444" },
  { n: "06", icon: "📈", title: "Optimise & Operate", desc: "Post-migration FinOps reviews, autoscaling tuning, SLO tracking, on-call runbooks, and quarterly architecture reviews to keep your cloud lean and reliable.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "PaySwift Cloud Migration",
    industry: "FinTech",
    desc: "Migrated a monolithic payment gateway serving 2M daily transactions to a Kubernetes-native microservices architecture on AWS — achieving 99.99% uptime and cutting infrastructure costs by 42%.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop&auto=format",
    tags: ["AWS EKS", "Terraform", "ArgoCD", "Istio"],
    metrics: [{ val: "99.99%", lbl: "Uptime SLA" }, { val: "42%", lbl: "Cost Reduction" }, { val: "0", lbl: "Downtime During Migration" }],
    color: "#0EA5E9",
  },
  {
    title: "MedVault DevSecOps",
    industry: "Healthcare",
    desc: "Built a HIPAA-compliant DevSecOps pipeline for a health-records SaaS platform — automated compliance checks, secrets management with Vault, and container image scanning reduced audit prep time from 3 weeks to 2 days.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop&auto=format",
    tags: ["GCP", "Vault", "Trivy", "OPA", "Terraform"],
    metrics: [{ val: "93%", lbl: "Faster Audit Prep" }, { val: "0", lbl: "Critical CVEs in Prod" }, { val: "HIPAA", lbl: "Fully Compliant" }],
    color: "#10B981",
  },
  {
    title: "ShopGrid Platform Scale-Up",
    industry: "Retail & E-Commerce",
    desc: "Designed multi-region AWS architecture with auto-scaling Kubernetes clusters for a 10M-user e-commerce platform — surviving a 20× traffic spike on Black Friday with zero degradation.",
    img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop&auto=format",
    tags: ["AWS", "Kubernetes", "Cloudflare", "Redis"],
    metrics: [{ val: "20×", lbl: "Traffic Spike Handled" }, { val: "99.95%", lbl: "Black Friday Uptime" }, { val: "38%", lbl: "Infra Cost Cut" }],
    color: "#8B5CF6",
  },
];

const FAQS = [
  {
    q: "How long does a cloud migration typically take?",
    a: "It depends on complexity. A single application migration can be completed in 4–8 weeks. A full enterprise migration with dozens of services typically takes 3–9 months. We always start with a cloud readiness assessment and phased roadmap — migrating lower-risk workloads first to build confidence and momentum before tackling critical production systems.",
  },
  {
    q: "Can you migrate us without any downtime?",
    a: "Yes, for the vast majority of workloads. We use blue-green deployments, database replication with cutover windows, and canary releases to achieve near-zero downtime migrations. For stateful databases, we plan maintenance windows during off-peak hours that are typically 5–15 minutes. We've migrated payment gateways and healthcare platforms with zero customer-facing downtime.",
  },
  {
    q: "Which cloud provider should we choose — AWS, GCP, or Azure?",
    a: "We're cloud-agnostic and will recommend based on your stack, team expertise, compliance needs, and cost profile. AWS is best for breadth of services and startup ecosystems. GCP excels for data/ML-heavy workloads. Azure is the natural fit if you're a Microsoft-heavy organisation. Many clients use multi-cloud for resilience — we design and manage all three.",
  },
  {
    q: "How do you handle security and compliance (SOC2, HIPAA, PCI)?",
    a: "Security is built into every layer — not bolted on at the end. We implement compliance-as-code using Open Policy Agent, secrets management with HashiCorp Vault, automated container scanning, RBAC with least privilege, encrypted storage, and audit logging. We've delivered SOC2 Type II, HIPAA-compliant, and PCI-DSS environments and can produce compliance evidence automatically from your pipelines.",
  },
  {
    q: "What is Infrastructure as Code and why do we need it?",
    a: "Infrastructure as Code (IaC) means your servers, networks, and cloud resources are defined in version-controlled code (Terraform or Pulumi) rather than configured manually. This eliminates environment drift, makes infrastructure reproducible across dev/staging/production, enables peer review and rollback, and reduces provisioning time from days to minutes. It's the foundation of reliable DevOps.",
  },
  {
    q: "Do you offer ongoing support after the migration?",
    a: "Yes. We offer managed DevOps retainers that include 24/7 on-call infrastructure support, monthly FinOps reviews, security patch management, capacity planning, SLO reporting, and quarterly architecture reviews. Most clients see their cloud bills continue to drop for 6–12 months post-migration as we continuously optimise reserved instances and autoscaling policies.",
  },
];

const STATS = [
  { val: "150+", lbl: "Cloud Projects Delivered", icon: "☁️" },
  { val: "99.99%", lbl: "Average Uptime SLA", icon: "⚡" },
  { val: "40%", lbl: "Avg Cost Reduction", icon: "📉" },
  { val: "0", lbl: "Production Outages", icon: "🛡️" },
];

const USE_CASES = [
  { industry: "FinTech", icon: "💳", examples: ["PCI-DSS compliant cloud", "High-frequency trading infra", "Real-time fraud pipeline"], color: "#0EA5E9" },
  { industry: "Healthcare", icon: "🏥", examples: ["HIPAA-compliant DevOps", "Medical data lake on GCP", "Zero-trust hospital network"], color: "#10B981" },
  { industry: "E-Commerce", icon: "🛍️", examples: ["Auto-scaling for peak traffic", "Multi-region CDN setup", "Black Friday readiness"], color: "#F59E0B" },
  { industry: "EdTech", icon: "🎓", examples: ["Multi-tenant SaaS infra", "Live-streaming at scale", "Global CDN for LMS"], color: "#8B5CF6" },
  { industry: "SaaS", icon: "💻", examples: ["Kubernetes multi-tenancy", "CI/CD for 50+ microservices", "SOC2 automation"], color: "#EF4444" },
  { industry: "Logistics", icon: "🚚", examples: ["IoT data ingestion pipeline", "Real-time tracking infra", "Edge computing at warehouses"], color: "#EC4899" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Services" button if we are on the web-development page
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        // Highlight "Web Development" inside the dropdown specifically
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "#"} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif" }}>{l.label}</a>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}
// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["99.99% Uptime SLA", "40% Cost Reduction", "Zero-Downtime Deployments", "150+ Cloud Projects"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 96,
      background: "linear-gradient(160deg,#0C1A2E 0%,#0C4A6E 35%,#075985 70%,#020617 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Animated background particles */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[...Array(20)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            borderRadius: "50%",
            background: `rgba(14,165,233,${Math.random() * 0.4 + 0.1})`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `pulse ${Math.random() * 3 + 2}s ease-in-out infinite`,
          }} />
        ))}
      </div>
      {/* Grid overlay */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(14,165,233,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(14,165,233,.07) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />
      {/* Glow blobs */}
      <div style={{ position: "absolute", top: -120, right: -120, width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle,rgba(14,165,233,.18),transparent 65%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(6,182,212,.12),transparent 65%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "rgba(255,255,255,.5)" }}>
          <Link to="/" style={{ color: "#38BDF8", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span><span>Services</span><span>›</span>
          <span style={{ color: "#fff", fontWeight: 600 }}>Cloud DevOps</span>
        </div>

        <div className="devops-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(14,165,233,.2)", border: "1px solid rgba(56,189,248,.4)", borderRadius: 99, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#38BDF8", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#7DD3FC" }}>☁️ India's Leading Cloud DevOps Company</span>
            </div>

            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,4.5vw,58px)", fontWeight: 900, color: "#fff", lineHeight: 1.1, marginBottom: 20 }}>
              Ship Faster. Scale Smarter.<br />
              <span style={{ background: "linear-gradient(135deg,#38BDF8,#06B6D4,#34D399)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Stay Always On.
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "rgba(255,255,255,.7)", lineHeight: 1.8, maxWidth: 560, marginBottom: 36 }}>
              We design, build, and manage cloud-native infrastructure and DevOps pipelines — from zero-downtime migrations and Kubernetes orchestration to IaC, DevSecOps, and 24/7 cloud operations.
            </p>

            {/* Rotating metric */}
            <div style={{ background: "rgba(14,165,233,.15)", border: "1px solid rgba(56,189,248,.3)", borderRadius: 12, padding: "14px 20px", marginBottom: 32, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#7DD3FC", transition: "all .4s" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#0369A1,#0EA5E9)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 24px rgba(14,165,233,.5)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 32px rgba(14,165,233,.6)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(14,165,233,.5)"; }}
              >🚀 Start Cloud Journey</a>
              <a href="#case-studies" style={{
                background: "rgba(255,255,255,.08)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "1px solid rgba(255,255,255,.2)",
                transition: "all .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.15)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; }}
              >📊 See Case Studies</a>
            </div>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[["150+", "Cloud Projects"], ["99.99%", "Avg Uptime"], ["40%", "Cost Reduction"], ["0", "Prod Outages"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#38BDF8" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)", fontWeight: 600 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cloud Visual */}
          <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {/* Central cloud */}
            <div style={{
              width: 260, height: 260, borderRadius: "50%",
              background: "radial-gradient(circle,rgba(14,165,233,.3),rgba(7,89,133,.1))",
              border: "2px solid rgba(56,189,248,.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 96, animation: "float 4s ease-in-out infinite",
              boxShadow: "0 0 80px rgba(14,165,233,.25)",
              position: "relative", zIndex: 2,
            }}>☁️</div>

            {/* Orbiting capability chips */}
            {[
              { label: "AWS / GCP / Azure", angle: 0, color: "#38BDF8" },
              { label: "Kubernetes", angle: 60, color: "#60A5FA" },
              { label: "Terraform IaC", angle: 120, color: "#34D399" },
              { label: "CI/CD Pipelines", angle: 180, color: "#F472B6" },
              { label: "DevSecOps", angle: 240, color: "#FBBF24" },
              { label: "FinOps", angle: 300, color: "#F87171" },
            ].map((chip, i) => {
              const rad = (chip.angle * Math.PI) / 180;
              const r = 180;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              return (
                <div key={chip.label} style={{
                  position: "absolute",
                  left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)`,
                  transform: "translate(-50%,-50%)",
                  background: "rgba(2,6,23,.85)",
                  border: `1px solid ${chip.color}50`,
                  borderRadius: 99,
                  padding: "7px 14px",
                  fontSize: 12, fontWeight: 700,
                  color: chip.color,
                  whiteSpace: "nowrap",
                  boxShadow: `0 4px 20px ${chip.color}25`,
                  zIndex: 3,
                  animation: `float ${3.5 + i * 0.3}s ease-in-out infinite ${i * 0.4}s`,
                }}>{chip.label}</div>
              );
            })}

            {/* Floating metric cards */}
            <div style={{ position: "absolute", top: -10, right: -24, background: "#fff", borderRadius: 16, padding: "12px 18px", boxShadow: "0 12px 40px rgba(14,165,233,.25)", border: "1.5px solid #BAE6FD", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0369A1" }}>99.99%</div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500 }}>Uptime SLA ⚡</div>
            </div>
            <div style={{ position: "absolute", bottom: 20, right: -20, background: "linear-gradient(135deg,#0369A1,#0EA5E9)", borderRadius: 16, padding: "13px 18px", boxShadow: "0 12px 36px rgba(14,165,233,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff" }}>0</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 500 }}>Prod Outages 🛡️</div>
            </div>
            <div style={{ position: "absolute", bottom: 40, left: -16, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 8px 28px rgba(0,0,0,.15)", border: "1.5px solid #D1FAE5", animation: "float 4s ease-in-out infinite 1s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>📉</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#065F46", fontFamily: "'Sora',sans-serif" }}>40% Cost Cut</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>Avg per cloud project</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#020617", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="devops-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{s.val}</div>
            <div style={{ color: "#64748B", fontSize: 13, marginTop: 6, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "96px 40px", background: "#fff" }} className="devops-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0369A1", letterSpacing: ".1em", textTransform: "uppercase" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Cloud DevOps Services</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            From cloud migration and Kubernetes orchestration to IaC, DevSecOps, and cost optimisation — we build infrastructure that never sleeps.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? s.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 24px 64px ${s.color}22` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s ease",
                transform: hov === i ? "translateY(-6px)" : "none",
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${s.color}25` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}12`, color: s.color, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, border: `1px solid ${s.color}25` }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .2s" }}>Explore Service →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Use Cases by Industry ─────────────────────────────────────────────────────
function UseCases() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="devops-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0369A1", letterSpacing: ".1em", textTransform: "uppercase" }}>Industries</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Cloud DevOps Across Every Industry</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Domain-specific cloud architecture that speaks your industry's compliance language and solves your scale challenges.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {USE_CASES.map((uc, i) => (
            <div key={uc.industry}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "28px 24px",
                border: `2px solid ${hov === i ? uc.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${uc.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `${uc.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, border: `1px solid ${uc.color}20` }}>
                  {uc.icon}
                </div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A" }}>{uc.industry}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {uc.examples.map(ex => (
                  <div key={ex} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: uc.color, flexShrink: 0, display: "inline-block" }} />
                    <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{ex}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="devops-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0369A1", letterSpacing: ".1em", textTransform: "uppercase" }}>Tech Stack</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Our Cloud & DevOps Toolkit</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>The battle-tested platforms, tools, and frameworks powering our cloud infrastructure solutions.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#0C4A6E,#0EA5E9)" : "#fff",
              borderColor: active === i ? "#0EA5E9" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(14,165,233,.35)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 20,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 12px 36px ${tech.color}28` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .28s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-6px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}15` : "#fff", border: `1px solid ${hov === i ? tech.color + "30" : "#E5E7EB"}`, boxShadow: hov === i ? `0 4px 16px ${tech.color}25` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .28s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .28s", transform: hov === i ? "scale(1.1)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 2 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 18px 18px", background: `linear-gradient(90deg,${tech.color},${tech.color}80)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "96px 40px", background: "linear-gradient(160deg,#0C1A2E 0%,#0C4A6E 100%)" }} className="devops-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#38BDF8", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#fff", marginTop: 8, marginBottom: 16 }}>From Legacy Infra to Cloud-Native</h2>
          <p style={{ color: "rgba(255,255,255,.6)", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>A rigorous, transparent 6-stage methodology that gets your workloads to the cloud — and keeps them running flawlessly.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "rgba(255,255,255,.06)", borderRadius: 20, padding: "32px 28px",
              border: `1px solid rgba(255,255,255,.1)`,
              backdropFilter: "blur(10px)",
              position: "relative", overflow: "hidden",
              transition: "all .3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(14,165,233,.15)"; e.currentTarget.style.borderColor = `${step.color}60`; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.1)"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: "rgba(255,255,255,.04)", lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${step.color}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${step.color}40` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}20`, padding: "3px 10px", borderRadius: 99, display: "inline-block", marginBottom: 10 }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#fff", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "rgba(255,255,255,.6)", fontSize: 14, lineHeight: 1.75 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}50)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "96px 40px", background: "#fff" }} className="devops-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0EA5E9", letterSpacing: ".1em", textTransform: "uppercase" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Cloud That Moved the Needle</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Real numbers from real deployments — production cloud infrastructure in the wild, not slide-deck architecture.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 60 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 52, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="devops-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}25` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 360, objectFit: "cover", display: "block", transition: "transform .5s", transform: hov === cs.title ? "scale(1.04)" : "scale(1)" }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 99 }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, color: "#0F172A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 28 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}12`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, border: `1px solid ${cs.color}25` }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 28, marginBottom: 32, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 26, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 4 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 26px", borderRadius: 10, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}35`,
                  transition: "transform .2s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#0369A1", border: "2px solid #BAE6FD",
            padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .2s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#E0F2FE"; e.currentTarget.style.borderColor = "#7DD3FC"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#BAE6FD"; }}
          >View All Cloud Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="devops-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0369A1", letterSpacing: ".1em", textTransform: "uppercase" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16 }}>Honest answers to the most common questions about cloud migration and DevOps projects.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16,
              border: `2px solid ${open === i ? "#0EA5E9" : "#E5E7EB"}`,
              overflow: "hidden", transition: "border-color .25s",
              boxShadow: open === i ? "0 8px 32px rgba(14,165,233,.1)" : "none",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 28px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.4, flex: 1, marginRight: 16 }}>{faq.q}</span>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: open === i ? "#0EA5E9" : "#F1F5F9",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .25s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#6B7280", fontSize: 18, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .25s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 28px 24px", animation: "fadeIn .2s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.8, borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `2px solid ${focused === n ? "#0EA5E9" : "#E5E7EB"}`,
    background: focused === n ? "#E0F2FE" : "#FAFAFA",
    color: "#0F172A", outline: "none", fontFamily: "'Sora',sans-serif",
    transition: "all .2s", boxShadow: focused === n ? "0 0 0 4px rgba(14,165,233,.1)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "96px 40px", background: "linear-gradient(160deg,#F8FAFC,#E0F2FE)" }} className="devops-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0369A1", letterSpacing: ".1em", textTransform: "uppercase" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 14 }}>Start Your Cloud Journey Today</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.75 }}>Tell us about your infrastructure and goals — we'll deliver a free cloud readiness assessment within 48 hours.</p>
        </div>
        <div className="devops-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#0C1A2E,#0C4A6E,#0369A1)", borderRadius: 24, padding: "40px 36px", marginBottom: 24, boxShadow: "0 20px 60px rgba(3,105,161,.3)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative" }}>Free Cloud Readiness Assessment</h3>
              <p style={{ color: "rgba(255,255,255,.7)", fontSize: 14, lineHeight: 1.7, marginBottom: 32, position: "relative" }}>Share your current setup — we'll assess migration readiness, design an architecture, and estimate cost savings. No commitment required.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 20, padding: "28px", border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 18 }}>What You Get — Free</h4>
              {[
                ["☁️", "Cloud readiness assessment for your workloads"],
                ["🏗️", "Architecture recommendation & design"],
                ["💰", "Cost savings projection & ROI estimate"],
                ["🔒", "NDA signed before any discussion"],
                ["🗺️", "Migration roadmap & timeline"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 24, padding: "44px", boxShadow: "0 8px 48px rgba(0,0,0,.08)", border: "1px solid #F1F5F9" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "linear-gradient(135deg,#0369A1,#0EA5E9)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 40, boxShadow: "0 12px 36px rgba(14,165,233,.35)" }}>☁️</div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>Assessment Requested! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 28px" }}>Our cloud team will review your infrastructure and deliver a free readiness report within 48 hours.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", service: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#0369A1,#0EA5E9)", color: "#fff", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", marginBottom: 6 }}>Describe Your Cloud Challenge</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>The more detail you share, the more specific our readiness assessment will be.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="devops-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Work Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="devops-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Service Needed</label>
                      <select name="service" value={form.service} onChange={handleChange} onFocus={() => setFocused("service")} onBlur={() => setFocused(null)} style={{ ...iStyle("service"), appearance: "none", cursor: "pointer" }}>
                        <option value="">Select service…</option>
                        {["Cloud Migration", "CI/CD Pipeline", "Kubernetes Setup", "Infrastructure as Code", "DevSecOps", "Cloud Cost Optimisation", "Monitoring & Observability", "Not sure yet"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Current Infrastructure & Goals *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="Describe your current setup — on-premise or cloud, tech stack, pain points (downtime, costs, slow deploys), and what success looks like for your business." rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.7 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your infrastructure details and ideas are completely safe. We sign NDAs and never share your information.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "15px", borderRadius: 12,
                    background: loading ? "#7DD3FC" : "linear-gradient(135deg,#0369A1,#0EA5E9)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'Sora',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 24px rgba(14,165,233,.4)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "☁️ Get Free Cloud Assessment"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Company", links: ["About Us", "Portfolio", "Blog", "Careers", "Contact", "Privacy Policy"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Python & AI"] },
  ];
  return (
    <footer style={{ background: "#020617", color: "#fff", padding: "72px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#0EA5E9"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#7DD3FC"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #0F172A", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function CloudDevOps() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.4; } }

        .nav-links        { display:flex; align-items:center; gap:4px; }
        .nav-cta          { display:flex !important; }
        .mob-menu-btn     { display:none; }

        .devops-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .devops-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .devops-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .devops-contact-grid { display:grid; grid-template-columns:1fr 1.5fr; gap:48px; align-items:start; }
        .footer-grid         { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .devops-form-row     { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media (max-width:1100px) {
          .devops-hero-grid  { gap:40px; }
          .footer-grid       { grid-template-columns:1fr 1fr; gap:32px; }
          .devops-stats-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:900px) {
          .devops-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .devops-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .devops-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid         { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links           { display:none !important; }
          .nav-cta             { display:none !important; }
          .mob-menu-btn        { display:flex !important; }
        }
        @media (max-width:640px) {
          .footer-grid       { grid-template-columns:1fr; }
          .devops-stats-grid { grid-template-columns:repeat(2,1fr); gap:24px; }
          .devops-form-row   { grid-template-columns:1fr !important; }
        }
        @media (max-width:500px) { .topbar-right { display:none !important; } }
        @media (max-width:900px) { .devops-section { padding-left:20px !important; padding-right:20px !important; } }
        @media (max-width:640px) { .devops-section { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; } }
      `}</style>

      <Hero />
      <StatsBar />
      <ServicesGrid />
      <UseCases />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}